import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { Select, SelectItem } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useAsyncList } from '@react-stately/data';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useInfiniteProducts } from '@/hooks/queries/useProducts';
import { Product } from '@/services/apis/products';

export interface ProductSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  showAllOption?: boolean;
  showEmptyOption?: boolean;
  'aria-label'?: string;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  value,
  onChange,
  placeholder,
  isInvalid = false,
  errorMessage,
  isRequired = false,
  label,
  variant = 'bordered',
  className,
  showAllOption = false,
  showEmptyOption = !showAllOption,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation('admin');
  const scrollRef = useRef<HTMLElement>(null);
  const spinnerRef = useRef<HTMLElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useInfiniteProducts({
    limit: 20,
  });

  const products = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || []
    );
  }, [data]);

  const itemsWithAll = useMemo(() => {
    const items: Product[] = [];
    if (showAllOption) {
      items.push({ id: 'all', name: t('guides.filters.all') } as Product);
    }
    if (showEmptyOption) {
      items.push({ id: '', name: t('common.none') || 'None' } as Product);
    }
    return [...items, ...products];
  }, [products, showAllOption, showEmptyOption, t]);

  const list = useAsyncList<Product>({
    async load({ cursor }: { cursor?: string }) {
      if (cursor) {
        await fetchNextPage();
      }
      return {
        items: itemsWithAll,
        cursor: hasNextPage ? 'next' : undefined,
      };
    },
  });

  // Reload list when products change to sync with React Query data
  useEffect(() => {
    if (itemsWithAll.length > 0) {
      list.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsWithAll.length]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  if (error) {
    return (
      <Select
        label={label || (label === undefined ? t('products.selector.label') : undefined)}
        placeholder={placeholder || t('products.selector.placeholder')}
        isRequired={isRequired}
        variant={variant}
        isInvalid={true}
        errorMessage={error?.message || 'Failed to load products'}
        isDisabled
        className={className}
      >
        <SelectItem key="error">{error?.message || 'Failed to load products'}</SelectItem>
      </Select>
    );
  }

  const selectLabel = label;
  const selectPlaceholder = placeholder || t('products.selector.placeholder');
  const selectAriaLabel =
    ariaLabel ||
    t('products.selector.label', { defaultValue: 'Products' }) ||
    (label === '' ? selectPlaceholder : undefined);

  return (
    <Select
      label={selectLabel}
      placeholder={selectPlaceholder}
      selectedKeys={value !== undefined && value !== null ? [value] : []}
      onSelectionChange={(keys) => {
        const selectedValue = Array.from(keys)[0] as string;
        onChange(selectedValue ?? '');
      }}
      isRequired={isRequired}
      variant={variant}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      className={className}
      selectionMode="single"
      isLoading={isLoading}
      items={list.items}
      scrollRef={scrollRef}
      spinnerRef={spinnerRef}
      aria-label={selectAriaLabel}
    >
      {(product) => <SelectItem key={product.id}>{product.name}</SelectItem>}
    </Select>
  );
};

export default ProductSelector;
